import type {
  AssessmentDto,
  AssessmentHistoryDto,
  AssessmentTemplateDto,
  PriorityDto,
  PriorityListDto,
  PriorityTier,
  PriorityWeightsDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, query } from '../../lib/api.js';

export interface PriorityFilters {
  productId?: string;
  marketId?: string;
  tier?: string;
  search?: string;
  overriddenOnly?: boolean;
  sort?: 'score' | 'tier' | 'expiry';
}

/** The prioritised org×product list («hvem ringer vi»). */
export function usePriorities(filters: PriorityFilters) {
  return useQuery({
    queryKey: ['priorities', filters],
    queryFn: () => api.get<PriorityListDto>(`/sales/priority${query({ ...filters })}`),
  });
}

export function usePriorityWeights() {
  return useQuery({
    queryKey: ['priority-weights'],
    queryFn: () => api.get<PriorityWeightsDto[]>('/sales/priority/weights'),
  });
}

export interface WeightsInput {
  contractExpiry: number;
  size: number;
  fit: number;
  relationship: number;
  signals: number;
}

function invalidatePriorities(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['priorities'] });
  void qc.invalidateQueries({ queryKey: ['priority-weights'] });
}

export function useUpdateWeights(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WeightsInput) =>
      api.put<PriorityWeightsDto>(`/sales/priority/weights/${productId}`, body),
    onSuccess: () => invalidatePriorities(qc),
  });
}

export function useRecompute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId?: string) =>
      api.post<{ computed: number; productId: string | null }>('/sales/priority/recompute', {
        productId,
      }),
    onSuccess: () => invalidatePriorities(qc),
  });
}

export interface SetTierInput {
  organizationId: string;
  productId: string;
  tier: PriorityTier | null;
  reason?: string | null;
}

export function useSetTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, productId, tier, reason }: SetTierInput) =>
      api.post<{ ok: boolean }>(`/sales/priority/${organizationId}/${productId}/tier`, {
        tier,
        reason,
      }),
    onSuccess: () => invalidatePriorities(qc),
  });
}

// ── Situasjonskartlegging ──────────────────────────────────────────────────────

export function useAssessmentHistory(organizationId: string | null, productId: string | null) {
  return useQuery({
    queryKey: ['assessments', organizationId, productId],
    queryFn: () =>
      api.get<AssessmentHistoryDto>(
        `/sales/assessments${query({ organizationId: organizationId ?? '', productId: productId ?? '' })}`,
      ),
    enabled: Boolean(organizationId && productId),
  });
}

function invalidateAssessments(
  qc: ReturnType<typeof useQueryClient>,
  organizationId: string,
  productId: string,
) {
  void qc.invalidateQueries({ queryKey: ['assessments', organizationId, productId] });
  void qc.invalidateQueries({ queryKey: ['priorities'] });
}

export function useKartlegg(organizationId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instruction?: string | null) =>
      api.post<AssessmentDto>('/sales/assessments/kartlegg', {
        organizationId,
        productId,
        instruction,
      }),
    onSuccess: () => invalidateAssessments(qc, organizationId, productId),
  });
}

export interface ConfirmInput {
  result?: Record<string, string>;
  freeText?: string | null;
  sources?: { label: string; url: string }[];
  fitScore?: number | null;
  fitRationale?: string | null;
}

export function useConfirmAssessment(organizationId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ConfirmInput }) =>
      api.post<AssessmentDto>(`/sales/assessments/${id}/confirm`, body),
    onSuccess: () => invalidateAssessments(qc, organizationId, productId),
  });
}

// ── Kartleggingsmaler (SALES:ADMIN) ────────────────────────────────────────────

export function useAssessmentTemplates(productId?: string) {
  return useQuery({
    queryKey: ['assessment-templates', productId ?? null],
    queryFn: () =>
      api.get<AssessmentTemplateDto[]>(`/sales/assessment-templates${query({ productId })}`),
  });
}

export function useTemplateVersions(productId: string | null) {
  return useQuery({
    queryKey: ['assessment-template-versions', productId],
    queryFn: () =>
      api.get<AssessmentTemplateDto[]>(`/sales/assessment-templates/${productId}/versions`),
    enabled: Boolean(productId),
  });
}

export interface CreateTemplateInput {
  productId: string;
  name: string;
  questions: { key: string; label: string; hint?: string | null }[];
  suggestedPrompt?: string | null;
  approvePrompt: boolean;
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTemplateInput) =>
      api.post<AssessmentTemplateDto>('/sales/assessment-templates', body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['assessment-templates'] });
      void qc.invalidateQueries({ queryKey: ['assessment-template-versions', vars.productId] });
    },
  });
}

export function useSuggestPrompt() {
  return useMutation({
    mutationFn: ({ productId, instruction }: { productId: string; instruction?: string | null }) =>
      api.post<{ prompt: string }>('/sales/assessment-templates/suggest-prompt', {
        productId,
        instruction,
      }),
  });
}
